
import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  ExternalLink,
  PaintBucket,
  Image,
  File,
  Pen,
  Import,
  LogOut,
  Users,
  User,
  ChevronDown,
} from "lucide-react";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
  SidebarSeparator,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Avatar } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

export const AdminLayout = ({ children }: { children?: React.ReactNode }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    toast.success("You have been signed out");
    navigate('/admin/login');
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen">
        <Sidebar>
          <SidebarHeader className="py-6 px-2">
            <div className="flex justify-center">
              <Link to="/admin/dashboard">
                <div className="flex px-4 items-center space-x-2">
                  <Image size={20} className="text-edgefi-purple" />
                  <span className="text-lg font-semibold">edgefi Admin</span>
                </div>
              </Link>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname === "/admin/dashboard"}
                  asChild
                >
                  <Link to="/admin/dashboard">
                    <LayoutDashboard size={20} />
                    <span>Dashboard</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname.includes("/admin/blog") && !pathname.includes("import")}
                  asChild
                >
                  <Link to="/admin/blog">
                    <Pen size={20} />
                    <span>Blog</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname === "/admin/content"}
                  asChild
                >
                  <Link to="/admin/content">
                    <FileText size={20} />
                    <span>Content</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname === "/admin/redirects"}
                  asChild
                >
                  <Link to="/admin/redirects">
                    <ExternalLink size={20} />
                    <span>Redirects</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="flex space-x-2"
                    isActive={pathname === "/admin/users"}
                    asChild
                  >
                    <Link to="/admin/users">
                      <Users size={20} />
                      <span>Users</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarSeparator />

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname === "/admin/wordpress-import"}
                  asChild
                >
                  <Link to="/admin/wordpress-import">
                    <Import size={20} />
                    <span>WordPress Import</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="flex space-x-2"
                  isActive={pathname === "/admin/brand-book"}
                  asChild
                >
                  <Link to="/admin/brand-book">
                    <PaintBucket size={20} />
                    <span>Brand Book</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter className="p-4">
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-full flex justify-between items-center">
                    <div className="flex items-center">
                      <Avatar className="h-8 w-8 mr-2 bg-edgefi-amethyst/20 text-edgefi-purple">
                        {user.profile?.avatar_url ? (
                          <img src={user.profile.avatar_url} alt={user.profile?.display_name || 'User'} />
                        ) : (
                          <span>{(user.profile?.display_name || 'U')[0]}</span>
                        )}
                      </Avatar>
                      <div className="text-left">
                        <div className="text-sm font-medium truncate w-32">
                          {user.profile?.display_name || user.email}
                        </div>
                        {user.roles && user.roles.length > 0 && (
                          <div className="text-xs text-gray-500">
                            {user.roles[0]}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/admin/profile" className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </SidebarFooter>
        </Sidebar>

        <div className="flex-1 bg-gray-50">
          <div className="flex justify-between items-center p-4 bg-white border-b">
            <div className="flex items-center">
              <SidebarTrigger className="mr-2" />
              <h1 className="text-xl font-semibold">
                {pathname === "/admin/dashboard" && "Dashboard"}
                {pathname === "/admin/blog" && "Blog Manager"}
                {pathname.includes("/admin/blog/") && "Blog Editor"}
                {pathname === "/admin/content" && "Content Manager"}
                {pathname === "/admin/redirects" && "URL Redirects"}
                {pathname === "/admin/wordpress-import" && "WordPress Import"}
                {pathname === "/admin/brand-book" && "Brand Book"}
                {pathname === "/admin/users" && "User Management"}
                {pathname === "/admin/profile" && "Profile Settings"}
              </h1>
            </div>
            <div>
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm" className="flex items-center">
                  <File className="mr-2 h-4 w-4" />
                  View Site
                </Button>
              </Link>
            </div>
          </div>
          <div className="p-6">
            {children || <Outlet />}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};
